import React, { useEffect, useState } from 'react'
// import { PoseController } from '../ai/pose-controller'
// import { telemetry } from '../lib/telemetry'
import { useAuth } from '../auth/AuthProvider'
// import { beep } from '../lib/beep'
import { Dashboard as DashboardView } from './Dashboard'
// Lazy-load heavier tabs and modals to keep the main chunk small
const Challenges = React.lazy(() => import('./Challenges').then(m => ({ default: m.Challenges })))
const Social = React.lazy(() => import('./Social').then(m => ({ default: m.Social })))
const Profile = React.lazy(() => import('./Profile').then(m => ({ default: m.Profile })))
const Activity = React.lazy(() => import('./Activity').then(m => ({ default: m.Activity })))
const SessionModal = React.lazy(() => import('./SessionModal').then(m => ({ default: m.SessionModal })))
import { IconChart, IconPlay, IconTrophy, IconUser, IconUsers, IconMore } from '../components/icons'
import { store } from '../lib/store'
import { enqueue as enqueueSync, drain as drainSync, setupSync, onSync } from '../lib/sync'
import { useToast } from '../components/Toast'
import { Confetti } from '../components/Confetti'
import { store as sessionStore } from '../lib/store'
import { InstallPrompt } from '../components/InstallPrompt'
import { notifyFriends, fetchNewNotifications, markAllSeen } from '../lib/notifications'
import { HelpForm } from './HelpForm'
import { UpdatesLog } from './UpdatesLog'
import { isAdmin as checkAdmin } from '../lib/admin'
const AdminTools = React.lazy(() => import('./AdminTools').then(m => ({ default: m.default })))
import { getBestSet, setBestSet, updatePrStreak } from '../lib/pr'
import { fetchRecentSessions } from '../lib/remoteSessions'
export default function App() {
  const { user, signOut } = useAuth()
  const { push } = useToast()
  // Controller is managed inside SessionModal for Phase 1
  const [tab, setTab] = useState<'dashboard'|'challenges'|'social'|'profile'>('dashboard')
  const [sessionOpen, setSessionOpen] = useState(false)
  const [activityOpen, setActivityOpen] = useState(false)
  const [confettiKey, setConfettiKey] = useState(0)
  const [unread, setUnread] = useState(0)
  const [admin, setAdmin] = useState(false)
  const [adminOpen, setAdminOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = React.useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current) return
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('click', onDoc)
    return () => document.removeEventListener('click', onDoc)
  }, [])

  useEffect(() => {
    setupSync()
    let lastSyncToast = 0
    const off = onSync((evt) => {
      const now = Date.now()
      if (evt.type === 'drain-success') {
        // Show at most once every 10s to avoid spam on multi-item drains
        if (now - lastSyncToast > 10000) {
          push(`Synced ${evt.count} session${evt.count>1?'s':''}`)
          lastSyncToast = now
        }
      } else {
        push('Sync error — will retry')
      }
    })
    // Poll lightweight notifications every 30s
    const poll = setInterval(async () => {
      const items = await fetchNewNotifications()
      if (items.length > 0) {
        setUnread((u) => u + items.length)
        // Avoid spamming with multiple toasts; show a single summary
        const pr = items.filter(n=>n.type==='friend_pr').length
        const sess = items.filter(n=>n.type==='friend_session').length
        const badge = items.filter(n=>n.type==='friend_badge').length
        const parts = [] as string[]
        if (pr) parts.push(`${pr} PR`)
        if (sess) parts.push(`${sess} workout${sess>1?'s':''}`)
        if (badge) parts.push(`${badge} badge${badge>1?'s':''}`)
        if (parts.length) push(`Friends: ${parts.join(' · ')}`)
      }
    }, 30000)
    return () => { off(); clearInterval(poll) }
  }, [push])

  // Try an early background drain shortly after mount to push any queued sessions (helps cross-device view)
  useEffect(() => {
    const id = setTimeout(() => { drainSync().catch(()=>{}) }, 1500)
    return () => clearTimeout(id)
  }, [])

  useEffect(() => {
    (async () => { try { setAdmin(await checkAdmin()) } catch { /* ignore */ } })()
  }, [])

  // Removed per-frame polling from the home view; handled inside SessionModal

  return (
  <div className="app-shell" style={{paddingBottom:70}}>
      <header className="app-header">
        <div className="brand">
          <img src={new URL('../../PushTrack.comwhiteLogotransparent.png', import.meta.url).toString()} alt="PushTrack" style={{height:24, cursor:'pointer'}} onClick={()=>setTab('dashboard')} />
          <button className="btn btn-ghost" onClick={()=>setTab('dashboard')}>Dashboard</button>
        </div>
        <div className="controls">
        {user && (
          <span className="hud-chip user" style={{marginRight:8}}>
            {user.email ?? 'Account'}
          </span>
        )}
        {/* Notifications bell moved into Dashboard welcome card */}
  <button className="hud-chip" style={{marginRight:8}} title="Help & feedback" onClick={()=>setHelpOpen(true)}>Help</button>
  <button className="hud-chip" style={{marginRight:8}} title="About & updates" onClick={()=>setAboutOpen(true)}>About</button>
        <InstallPrompt />
        {/* Overflow menu: stable width, holds Admin and Sign out */}
        <div ref={menuRef} style={{position:'relative'}}>
          <button className="btn btn-ghost" aria-haspopup="menu" aria-expanded={menuOpen} onClick={(e)=>{ e.stopPropagation(); setMenuOpen(v=>!v) }} style={{padding:'6px 10px'}} title="More">
            <IconMore />
          </button>
          {menuOpen && (
            <div role="menu" className="menu-popover" style={{position:'absolute', right:0, top:'calc(100% + 6px)', minWidth:160, padding:6, zIndex:50}}>
              <button role="menuitem" className="btn btn-ghost" style={{display:'block', width:'100%', textAlign:'left', margin:0}} onClick={async()=>{
                try {
                  setMenuOpen(false)
                  await drainSync()
                  // Optional: kick a local fetch here if needed by listeners
                  const rows = await fetchRecentSessions(30)
                  // Broadcast a custom event so any view can update
                  window.dispatchEvent(new CustomEvent('pt_cloud_refresh', { detail: { rowsCount: rows.length } }))
                  push('Cloud refreshed')
                } catch {
                  push('Refresh failed')
                }
              }}>Refresh cloud</button>
              {admin && (
                <button role="menuitem" className="btn btn-ghost" style={{display:'block', width:'100%', textAlign:'left', margin:0}} onClick={()=>{ setAdminOpen(true); setMenuOpen(false) }}>Admin tools</button>
              )}
              {user && (
                <button role="menuitem" className="btn btn-ghost" style={{display:'block', width:'100%', textAlign:'left', marginTop:6}} onClick={()=>{ setMenuOpen(false); signOut() }}>Sign out</button>
              )}
            </div>
          )}
        </div>
        </div>
      </header>
      <main className="main">
        {confettiKey > 0 && <Confetti trigger={confettiKey} onDone={() => setConfettiKey(0)} />}
  {tab === 'dashboard' && <DashboardView
          unread={unread}
          onNotifications={()=>{ setTab('social'); setUnread(0); markAllSeen() }}
          onOpenActivity={() => setActivityOpen(true)}
        />}
        <React.Suspense fallback={<div className="list-item">Loading…</div>}>
          {tab === 'challenges' && <Challenges onStart={() => setSessionOpen(true)} />}
          {tab === 'social' && <Social />}
          {tab === 'profile' && <Profile />}
        </React.Suspense>
        {admin && adminOpen && (
          <div className="modal-backdrop" role="dialog" aria-modal="true">
            <div className="modal" style={{width:'min(96vw, 900px)'}}>
              <div className="modal-header">
                <div>Admin tools</div>
                <button className="btn btn-ghost" onClick={()=>setAdminOpen(false)}>Close</button>
              </div>
              <div className="modal-body">
                <React.Suspense fallback={<div className="list-item">Loading…</div>}>
                  <AdminTools hideHeader onClose={()=>setAdminOpen(false)} />
                </React.Suspense>
              </div>
            </div>
          </div>
        )}
        {helpOpen && (
          <div className="modal-backdrop" role="dialog" aria-modal="true">
            <div className="modal" style={{width:'min(96vw, 680px)'}}>
              <div className="modal-header">
                <div>Help & feedback</div>
                <button className="btn btn-ghost" onClick={()=>setHelpOpen(false)}>Close</button>
              </div>
              <div className="modal-body">
                <HelpForm onClose={()=>setHelpOpen(false)} />
              </div>
            </div>
          </div>
        )}
        {aboutOpen && (
          <div className="modal-backdrop" role="dialog" aria-modal="true">
            <div className="modal" style={{width:'min(96vw, 680px)'}}>
              <div className="modal-header">
                <div>About the app • Updates</div>
                <button className="btn btn-ghost" onClick={()=>setAboutOpen(false)}>Close</button>
              </div>
              <div className="modal-body">
                <UpdatesLog />
              </div>
            </div>
          </div>
        )}
      </main>
      {/* Side rail removed to avoid duplication with bottom navigation */}
      {/* Bottom nav bar for app-like feel */}
  <nav className="app-nav" style={{position:'fixed', left:0, right:0, bottom:0, height:`calc(var(--tabbar-h) + env(safe-area-inset-bottom))`, paddingBottom:'env(safe-area-inset-bottom)', background:'var(--panel)', borderTop:'1px solid rgba(255,255,255,0.08)', display:'grid', gridTemplateColumns:'repeat(5, 1fr)', alignItems:'center', zIndex:900, borderTopLeftRadius:16, borderTopRightRadius:16, boxShadow:'0 -8px 24px rgba(0,0,0,0.45)', backdropFilter:'saturate(1.1) blur(8px)'}} aria-label="Bottom navigation">
        <button className={tab==='dashboard'?'tab active':'tab'} onClick={()=>setTab('dashboard')} aria-label="Dashboard" style={{justifySelf:'center', display:'grid', placeItems:'center'}}>
          <IconChart className="ico" />
          <span className="lbl">Dashboard</span>
        </button>
        <button className={tab==='challenges'?'tab active':'tab'} onClick={()=>setTab('challenges')} aria-label="Challenges" style={{justifySelf:'center', display:'grid', placeItems:'center'}}>
          <IconTrophy className="ico" />
          <span className="lbl">Challenges</span>
        </button>
        {/* Start session in the center of the nav */}
        <button className={'tab tab-start start-cta'} onClick={()=>setSessionOpen(true)} aria-label="Start" style={{justifySelf:'center', display:'grid', placeItems:'center'}}>
          <IconPlay className="ico" />
          <span className="lbl">Start</span>
        </button>
        <button className={tab==='social'?'tab active':'tab'} onClick={()=>{ setTab('social'); setUnread(0); markAllSeen(); }} aria-label="Social" style={{justifySelf:'center', position:'relative', display:'grid', placeItems:'center'}}>
          <IconUsers className="ico" />
          <span className="lbl">Social</span>
          {unread > 0 && (
            <span aria-label={`${unread} unread`} style={{position:'absolute', top:2, right:14, background:'#e00', color:'#fff', borderRadius:12, padding:'0 6px', fontSize:10, lineHeight:'16px', minWidth:16, textAlign:'center'}}>{unread}</span>
          )}
        </button>
        <button className={tab==='profile'?'tab active':'tab'} onClick={()=>setTab('profile')} aria-label="Profile" style={{justifySelf:'center', display:'grid', placeItems:'center'}}>
          <IconUser className="ico" />
          <span className="lbl">Profile</span>
        </button>
      </nav>
      <React.Suspense fallback={null}>
      <SessionModal
        open={sessionOpen}
        onClose={()=>setSessionOpen(false)}
        onFinish={(summary) => {
          const id = Math.random().toString(36).slice(2)
          const sets = summary.sets.map((s, i) => ({ id: `${id}-set-${i+1}`, ...s }))
          const allBefore = sessionStore.all()
          const prevBest = Math.max(0, ...allBefore.flatMap(s => s.sets.map(se => se.reps)))
          // compute previous best-day (all-time) and today's total before adding this session
          const daySums: Record<string, number> = {}
          const todayKey = (ts:number) => { const d = new Date(ts); return `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}` }
          const today = todayKey(Date.now())
          for (const s of allBefore) {
            const k = todayKey(s.startedAt)
            daySums[k] = (daySums[k] || 0) + s.reps
          }
          const prevBestDay = Object.keys(daySums).length ? Math.max(...Object.values(daySums)) : 0
          const todayBefore = daySums[today] || 0
          store.add({ id, startedAt: Date.now(), endedAt: Date.now(), reps: summary.reps, sets })
          const newBest = Math.max(0, ...sets.map(s => s.reps))
          let confetti = true
          try { confetti = localStorage.getItem('pt_confetti') !== '0' } catch { /* ignore */ }
          let fired = false
          if (newBest > prevBest) {
            push('New PR!')
            if (confetti && !fired) { setConfettiKey(Date.now()); fired = true }
            // Proactively notify friends (server RPC fans out to friends list)
            (async () => { try { await notifyFriends('friend_pr', { best: newBest }) } catch (e) { /* ignore */ } })()
          }
          // Persist best-set PR
          if (newBest > getBestSet()) setBestSet(newBest)
          // Best-day check (today total after this session)
          const newToday = todayBefore + summary.reps
          if (newToday > prevBestDay) {
            push('New best day!')
            if (confetti && !fired) { setConfettiKey(Date.now()); fired = true }
          }
          // Update PR streak (only increments if PR occurred today)
          try { updatePrStreak(newBest > prevBest) } catch { /* ignore */ }
          // Fire-and-forget Supabase sync (user must be logged in; RLS will scope rows)
          (async () => {
            try {
              enqueueSync(summary)
              await drainSync()
            } catch (e) { console.warn('Supabase sync failed', e) }
          })()
          // Friend workout notification (lightweight)
          ;(async () => { try { await notifyFriends('friend_session', { reps: summary.reps }) } catch (e) { /* ignore */ } })()
          setSessionOpen(false)
          setTab('dashboard')
        }}
      />
      </React.Suspense>
      {/* Activity modal */}
      {activityOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal" style={{width:'min(96vw, 700px)'}}>
            <div className="modal-header">
              <div>Activity</div>
              <button className="btn btn-ghost" onClick={()=>setActivityOpen(false)}>Close</button>
            </div>
            <div className="modal-body">
              <Activity />
            </div>
          </div>
        </div>
      )}
  {/* Calibration removed: global, fixed thresholds are used for fairness */}
    </div>
  )
}

